import { api } from "../axios";
import { getClientById } from "../clients/client";
import {
  DistributorBasicInfo,
  DistributorProfile,
  PaginatedResponse,
  TransactionOrderType,
} from "../../types";

export interface UpdateDistributorProfilePayload {
  name?: string;
  gst_number?: string;
  handle?: string;
  euin?: string;
}

const updateDistributorProfile = async (
  data: UpdateDistributorProfilePayload
) => {
  return api.patch("distributor/profile/", data);
};

export const checkConsentRequestToAddClientStatusApi = async (
  clientId: number
): Promise<boolean> => {
  try {
    const client = await getClientById(clientId);
    return client.status === "SETUP_PENDING";
  } catch {
    return false;
  }
};

type GetClientsWithPendingMandatesResponse = PaginatedResponse<{
  id: number;
  investment_account_id: string;
  client_id: number;
  client_name: string;
  client_number: string;
  bank_account_id: number;
  bank_account_name: string;
  mandate_amount: number;
  mandate_type: string;
  mandate_status: string;
  created_at: string;
  updated_at: string;
}>;

export function getClientsWithPendingMandates({
  page,
  pageSize,
}: {
  page: number;
  pageSize: number;
}): Promise<GetClientsWithPendingMandatesResponse> {
  return api.get("investor-mandates/client_list/", {
    params: { page, page_size: pageSize, status: "PENDING" },
  });
}

export { updateDistributorProfile };

type GetClientsWithDraftOrdersResponse = PaginatedResponse<{
  investment_account_guid: string;
  client_id: number;
  client_name: string;
  client_number: string;
  total_amount: string;
  order_types: TransactionOrderType[];
}>;

export function getClientsWithDraftOrders({
  page,
  pageSize,
}: {
  page: number;
  pageSize: number;
}): Promise<GetClientsWithDraftOrdersResponse> {
  return api.get("draft-orders/client_list/", {
    params: { page, page_size: pageSize },
  });
}

export const getDistributorProfile = async (): Promise<DistributorProfile> => {
  return api.get("distributor/profile/");
};

export const getDistributorInfoByHandle = async (
  handle: string
): Promise<DistributorBasicInfo> => {
  return api.get(`distributor/by-handle/${handle}/`);
};

export interface DistributorBankDetails {
  account_number: string;
  ifsc_code: string;
  account_type: string;
  id?: number;
}

export const getDistributorBankAccountDetailsApi =
  (): Promise<DistributorBankDetails> => api.get("distributor/bank-account/");

export const createDistributorBankAccountDetailsApi = (
  payload: DistributorBankDetails
) => api.post("distributor/bank-account/", payload);

export const updateDistributorBankAccountDetailsApi = (
  payload: Partial<DistributorBankDetails>
) => api.put("distributor/bank-account/", payload);

export interface DistributorNomineeDetails {
  name: string;
  pan_card?: string;
  relationship: string;
  id: number;
  date_of_birth?: string;
}

export const getDistributorNomineeDetailsApi =
  (): Promise<DistributorNomineeDetails> => api.get("distributor/nominees/");

export const upsertDistributorNomineeDetailsApi = (
  payload: Omit<DistributorNomineeDetails, "id">
) => api.post("distributor/nominees/", payload);

export interface DistributorPreferences {
  id: number;
  distributor: number;
  idcw_payout_enabled: boolean;
  created_at: string | null;
  modified_at: string | null;
}

export const getDistributorPreferencesApi =
  (): Promise<DistributorPreferences> => api.get("distributor/preferences/");

export const updateDistributorPreferencesApi = (
  payload: Partial<Pick<DistributorPreferences, "idcw_payout_enabled">>
): Promise<DistributorPreferences> =>
  api.patch("distributor/preferences/", payload);

/*
 ******** Common *********
 */

export type PostOffice = {
  District: string;
  State: string;
};

type GetPincodeDetailsPayload = {
  Message: string;
  Status: string;
  PostOffice: PostOffice[];
}[];

export const getPincodeDetails = (
  pincode: string
): Promise<GetPincodeDetailsPayload> => api.get(`pincode/${pincode}`);

export interface ifscCodeDetailsPayload {
  BRANCH: string;
  ADDRESS: string;
  CITY: string;
  STATE: string;
  BANK: string;
}

export const getIfscCodeDetails = (
  code: string
): Promise<ifscCodeDetailsPayload> => api.get(`ifsc/${code}`);

interface BrokerageCard {
  scheme_name: string;
  amc_code: string;
  amc_name: string;
  morningstar_amc_code: string;
  year_1_rate: number;
  year_2_rate: number;
  year_3_rate: number;
  year_4_rate: number;
  year_5_rate: number;
  fund_id: string;
  isin: string;
}

export const getBrokerageCard = (
  page: number,
  pageSize: number,
  search: string,
  amcFilter: string[],
  productTypeFilter: string[] = [],
  sort?: Array<{ id: string; desc: boolean }>
): Promise<PaginatedResponse<BrokerageCard>> =>
  api.get("brokerage-card/", {
    params: {
      search,
      page_size: pageSize,
      page,
      morningstar_amc_code: amcFilter.join(","),
      product_type: productTypeFilter.join(","),
      ordering: sort?.length
        ? sort.map((s) => `${s.desc ? "-" : ""}${s.id}`).join(",")
        : undefined,
    },
  });
